import type { Command } from '../types/command';

/**
 * Select the candidate set that reaches the ranking strategy (§4f).
 *
 * Pure function of `(registry, rootCommandId, query)`. Doesn't touch
 * `priority` or `section` -- that's the strategy's job.
 *
 * Behaviour:
 *
 *   - **Empty query**: direct children of the current level.
 *     - Main mode (`rootCommandId === null`): every command with no
 *       `parent`.
 *     - Entered mode: commands whose `parent` is the entered command's
 *       id.
 *   - **Non-empty query**: the recursively-flattened descendant tree
 *     from the current level. Typing "dark" at top level surfaces
 *     "Dark mode" even though "Theme" isn't entered. (Kbar's
 *     `getDeepResults`.) Kills the need for an `inlineChildren` flag:
 *     a command's children appear in main-mode search whenever they're
 *     registered, and the consumer hook controls registration via
 *     its useQuery `enabled` gate.
 *
 * Excludes view commands from descendant walks when query is empty in
 * an entered mode: views are leaves with their own UI surface and
 * shouldn't appear nested inside another command's child list. They
 * remain top-level reachable by name + shortcut.
 */
export function selectCandidates(
  registry: Record<string, Command>,
  rootCommandId: string | null,
  query: string,
): Command[] {
  const hasQuery = query.trim() !== '';
  const all = Object.values(registry);

  if (!hasQuery) {
    // Direct children only.
    if (rootCommandId === null) {
      return all.filter((c) => c.parent === undefined);
    }
    return all.filter((c) => c.parent === rootCommandId);
  }

  // Non-empty query: deep descendants from the current level.
  if (rootCommandId === null) {
    // Main-mode deep search: every command in the registry. (Anything
    // unparented is at the top level; anything parented is a descendant
    // of some top-level node and therefore reachable.) Simpler and
    // faster than a tree walk -- the registry IS the flat closure.
    return all;
  }

  // Entered-mode deep search: descendants of `rootCommandId`.
  // Build a parent → children index once, then BFS from the root. The
  // index is local to this call (no caching); for the small N we deal
  // with, the allocation cost is negligible compared to render churn.
  const childrenByParent = new Map<string, Command[]>();
  for (const command of all) {
    if (command.parent === undefined) {
      continue;
    }
    let list = childrenByParent.get(command.parent);
    if (list === undefined) {
      list = [];
      childrenByParent.set(command.parent, list);
    }
    list.push(command);
  }

  const out: Command[] = [];
  const stack: string[] = [rootCommandId];
  const visited = new Set<string>();
  while (stack.length > 0) {
    const parentId = stack.pop() as string;
    const children = childrenByParent.get(parentId);
    if (children === undefined) {
      continue;
    }
    for (const child of children) {
      if (visited.has(child.id)) {
        continue;
      }
      visited.add(child.id);
      out.push(child);
      stack.push(child.id);
    }
  }
  return out;
}
